import React, { useState, useRef, useEffect, useLayoutEffect, useId } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./DateCustom.css";
import { Box, IconButton, InputAdornment, useTheme } from "@mui/material";
import { CssTextField } from "./CustomWidget";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useTranslation } from "react-i18next";

const DROPDOWN_GAP = 4; // px between the input and the calendar

export default function DateCustom({
  value,
  onChange,
  disabled,
  error,
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear())), // Default maxDate to one year in the past
  minDate = new Date(1900, 0, 1),
  className = "",
  inputId,
  // Opt-in, default false — see CssTextField's own `rewampTone` comment.
  // Only CustomWidget.js's CustomDatePicker (scheme-apply, via Mapper.js)
  // passes this; FarmerProfileEditFormComposer.js/FarmerRegistrationFormComposer.js
  // render DateCustom directly and never pass it, so they're unaffected.
  rewampTone = false,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const calendarRef = useRef(null);
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";
  const _inputId = useId();

  if(!inputId) inputId = _inputId;

  function handleChange(newDate) {
    if (disabled) return;
    setIsOpen(false);

    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, "0");
    const day = String(newDate.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day}`;
    onChange(formattedDate);
  }

  function toggleCalendar() {
    if (disabled) return;
    setIsOpen(!isOpen);
  }

  function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  }

  // Close calendar when clicking outside (the input or the portaled dropdown)
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        anchorRef.current?.contains(event.target) ||
        calendarRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // The dropdown is portaled to <body> and positioned from the input's
  // viewport rect, so an ancestor with `overflow: hidden` (e.g.
  // FormComponent's card) can't clip it. Flips above the input when there
  // isn't enough room below.
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return;

    const updatePosition = () => {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const calendarHeight = calendarRef.current?.offsetHeight || 350;
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const openUpward = spaceBelow < calendarHeight && anchorRect.top > spaceBelow;

      setPosition({
        left: anchorRect.left,
        width: anchorRect.width,
        top: openUpward
          ? anchorRect.top - calendarHeight - DROPDOWN_GAP
          : anchorRect.bottom + DROPDOWN_GAP,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  return (
    <Box className="date-picker-container" ref={anchorRef}>
      <CssTextField
        id={inputId}
        type="text"
        value={formatDate(value)}
        onClick={toggleCalendar}
        readOnly
        placeholder={t("SELECT_Date")}
        className={`${"date-input"} ${className}`}
        disabled={disabled}
        darkTheme={isDarkTheme}
        rewampTone={rewampTone}
        error={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" style={{ zIndex: 2 }}>
              <IconButton aria-label="calender icon">
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {isOpen &&
        createPortal(
          <div
            ref={calendarRef}
            className="calendar-dropdown open"
            style={{
              position: "fixed",
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              width: position?.width,
              zIndex: theme.zIndex.modal + 1,
              visibility: position ? "visible" : "hidden",
            }}
          >
            <Calendar
              date={value ? new Date(value) : maxDate} // Open the calendar at maxDate if no value is set
              onChange={handleChange}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>,
          document.body
        )}
    </Box>
  );
}
